import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './theme.css';

import { App } from './App';

// Сургуч: акцент направления «реестр сшитых документов» — цвет сургучной
// печати, скрепляющей подшивку. Используется только в статусах и точках
// подтверждения (подписано/отправлено/оплачено), не как декоративный цвет.
const seal: [string, string, string, string, string, string, string, string, string, string] = [
  '#fbeeec',
  '#f3d3ce',
  '#e6a89e',
  '#d87c6c',
  '#cc5941',
  '#c4432a',
  '#a8341f', // [6] — базовый тон акцента
  '#8a2a1e',
  '#6f2119',
  '#571a14',
];

// Графит: чернильно-серая шкала разметки реестра — линии, рамки, второстепенный текст.
const ledger: [string, string, string, string, string, string, string, string, string, string] = [
  '#f7f5f2',
  '#ece8e2',
  '#d9d3c9',
  '#bfb7a9',
  '#9c9284',
  '#7d7568',
  '#5e574c',
  '#453f37',
  '#2e2924',
  '#1c1815',
];

// Кожа: цвет переплёта — тёмно-коричневая обложка реестра, только для шапки.
const leather: [string, string, string, string, string, string, string, string, string, string] = [
  '#efe6dc',
  '#dcc8b0',
  '#c2a482',
  '#a5825c',
  '#8a6743',
  '#71512f',
  '#5c4026', // [6] — базовый тон переплёта
  '#4a3320',
  '#39271a',
  '#291b12',
];

const theme = createTheme({
  primaryColor: 'seal',
  primaryShade: 6,
  colors: { seal, ledger, leather },
  fontFamily: '"IBM Plex Sans", -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  fontFamilyMonospace: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace',
  headings: {
    fontFamily: '"IBM Plex Sans", -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    fontWeight: '600',
  },
  defaultRadius: 0,
  black: '#1c1815',
  white: '#fdfcfa',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Данные учётной системы меняются нечасто, но и устаревшие показывать
      // нельзя: минута — компромисс между свежестью и лишними запросами
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Не найден элемент #root');

createRoot(container).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <QueryClientProvider client={queryClient}>
        <Notifications position="top-right" />
        <App />
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);

// Service worker нужен, чтобы приложение ставилось на телефон иконкой.
// Только в продакшене: в разработке он кешировал бы старую сборку
// и правки не появлялись бы после перезагрузки.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js');
  });
}
